import React, { Children, useCallback, useMemo, useState } from 'react'
import capitalize from 'lodash/capitalize'
import upperCase from 'lodash/upperCase'
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { CurrencyDollar } from '@styled-icons/heroicons-outline'
import { ReactComponent as CalendarDay } from 'assets/CalendarDay.svg'
import { ReactComponent as CalendarXMark } from 'assets/CalendarXMark.svg'
import { ReactComponent as CalendarWeek } from 'assets/CalendarWeek.svg'
import { ReactComponent as CalendarMoney } from 'assets/CalendarMoney.svg'
import { ReactComponent as CalendarXMoney } from 'assets/CalendarXMoney.svg'
import { ReactComponent as MoneyCheckDollarPenIcon } from 'assets/MoneyCheckDollarPen.svg'

import { get } from 'utils/lodash'
import { useDispatch, shallowEqual, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'
import { selectIsInventoryFeatureEnabled } from 'data/tenant/tenantSettingsSelectors'
import { getConfiguredServiceDetailsPageUrl } from 'router/routes'
import { getServiceRecurrenceText } from 'components/locations/LocationsDataGrid/RenderLocationDetailPanelContent/ConfiguredServiceCard'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { CACHE_TAG_CONFIGURED_SERVICES_LIST, CACHE_TAG_CONFIGURED_SERVICE_DETAILS } from 'api/cacheTagTypes'
import { useCSDetailsCachedData } from 'components/configured-service/useCSDetailsCachedData'
import { selectAddress, selectLocationName } from 'data/address/addressSelectors'
import {
  selectMeasureUnit,
  selectMeasureLabel,
  selectMethodType,
  selectActionType,
  selectMaterialType,
  selectMaterialColor,
  selectStartDate,
  selectEndDate,
  selectBillingStartDate,
  selectBillingEndDate,
  selectPurchaseOrderNumber,
  selectPricingZoneName,
  selectPricingPeriodText,
  selectAccountId,
  selectLocation,
  selectContainers,
} from 'data/configured-service/configuredServiceDetailsSelectors'
import LocationMapImage from 'components/customer-details/content/location/LocationMapImage'
import ServiceActionTypeChip from 'components/services/ServiceActionTypeChip'
import ServiceMaterialTypeChip from 'components/services/ServiceMaterialTypeChip'
import InteractiveTagsList from 'components/tags/InteractiveTagsList'

import HHDisplayMoney from 'components/common/HHDisplayMoney'
import MethodChip, { METHOD_TO_ICON_MAP } from 'components/common/chip/MethodChip'
import MeasureChip from 'containers/new-route-manager/RouteCard/MeasureChip'
import PropTypes from 'prop-types'
import ServiceTypeAvatar from 'components/common/avatar/ServiceTypeAvatar'
import { TabPanel } from 'components/common'
import { StyledAccordion } from 'components/customer-details/content/work-orders/layout/StyledAccordion'
import WeeklySummary from 'components/customer-details/content/configured-services/actions/weekly-summary/WeeklySummary'
import DraftServiceActionMenu from 'components/customer-details/content/configured-services/draft/DraftServiceActionMenu'
import { getPricingPeriod } from 'utils/service'
import ActionBasedPricingIcon from 'components/configured-service/common/ActionBasedPricingIcon'
import ContainersChip from 'components/configured-service/common/ContainersChip'
import { AdjustServiceProvider, useAdjustService } from 'components/configured-service/layout/AdjustServiceContext'
import AdjustServicePanel from 'components/configured-service/layout/AdjustServicePanel'
import { CONFIGURED_SERVICES_EXPANDED_TABS } from 'components/customer-details/content/configured-services/settings'
import api from 'api'
import T from 'T'

const { SERVICE_DETAILS, EDIT } = CONFIGURED_SERVICES_EXPANDED_TABS

const listItemSx = { flex: 'inherit', width: 125 }

const DetailItemIcon = ({ children }) => <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>{children}</ListItemIcon>

const DetailItemText = ({ primary, secondary, ...rest }) => (
  <ListItemText
    sx={listItemSx}
    primary={primary}
    secondary={secondary}
    slotProps={{
      primary: { variant: 'subtitle1', fontWeight: 500 },
      secondary: { variant: 'caption' },
    }}
    {...rest}
  />
)

const DraftServiceContent = ({ serviceId }) => {
  const dispatch = useDispatch()
  const { data, isLoading } = useCSDetailsCachedData({ id: serviceId })

  const accountId = selectAccountId(data)
  const location = selectLocation(data)
  const { line1, unitNumber, city, state, zipCode, latitude, longitude } = selectAddress(location)
  const locationName = selectLocationName(location)

  const startDate = selectStartDate(data) || '--'
  const endDate = selectEndDate(data) || '--'
  const billingStartDate = selectBillingStartDate(data) || '--'
  const billingEndDate = selectBillingEndDate(data) || '--'
  const purchaseOrderNumber = selectPurchaseOrderNumber(data) || '--'
  const pricingZone = selectPricingZoneName(data) ?? '--'
  const pricingPeriod = selectPricingPeriodText(data) || '--'

  const measureUnit = selectMeasureUnit(data)
  const measureLabel = selectMeasureLabel(data)
  const methodType = selectMethodType(data)
  const actionType = selectActionType(data)
  const materialType = selectMaterialType(data)
  const materialColor = selectMaterialColor(data)
  const containers = selectContainers(data)

  const tagDetails = get(data, 'tagDetails', {})
  const serviceName = get(data, 'serviceName', '')

  const onSaveTags = (savedData, closeCallback) => {
    closeCallback()
    dispatch(api.util.invalidateTags([CACHE_TAG_CONFIGURED_SERVICES_LIST, { type: CACHE_TAG_CONFIGURED_SERVICE_DETAILS, id: serviceId }]))
  }

  return (
    <Grid container py={2} pr={2} columnGap={2}>
      <Grid size={{ xs: 12, md: 'grow' }}>
        <ListItem sx={{ pt: 0 }}>
          <ListItemText
            primary={
              <>
                <Typography variant="h6" fontWeight={500} noWrap>
                  {isLoading ? <Skeleton width={200} /> : line1}
                </Typography>
                {unitNumber && (
                  <Typography variant="h6" fontWeight={500} noWrap>
                    {unitNumber}
                  </Typography>
                )}
                <Typography variant="h6" fontWeight={500} noWrap>
                  {isLoading ? <Skeleton width={100} /> : [city, state, zipCode].filter(Boolean).join(', ')}
                </Typography>
              </>
            }
            secondary={isLoading ? <Skeleton width={150} /> : locationName}
            slotProps={{
              secondary: { variant: 'h6', fontWeight: 400, noWrap: true },
            }}
          />
        </ListItem>
        <ListItem>
          <InteractiveTagsList
            accountId={accountId}
            data={{ tagDetails, name: serviceName, configuredServiceId: serviceId }}
            onSave={onSaveTags}
            disableAccount
            disableLocation
          />
        </ListItem>
        <ListItem divider>
          <DetailItemIcon>
            <SvgIcon>
              <CalendarDay />
            </SvgIcon>
          </DetailItemIcon>
          <DetailItemText primary={isLoading ? <Skeleton width={50} /> : startDate} secondary={capitalize(T.START_DATE)} />
          <DetailItemIcon>
            <CalendarXMark />
          </DetailItemIcon>
          <DetailItemText primary={endDate} secondary={capitalize(T.END_DATE)} />
        </ListItem>
        <ListItem>
          <DetailItemIcon>
            <SvgIcon>
              <CalendarMoney />
            </SvgIcon>
          </DetailItemIcon>
          <DetailItemText primary={isLoading ? <Skeleton width={50} /> : billingStartDate} secondary={capitalize(T.BILLING_START_DATE)} />
          <DetailItemIcon>
            <SvgIcon>
              <CalendarXMoney />
            </SvgIcon>
          </DetailItemIcon>
          <DetailItemText primary={isLoading ? <Skeleton width={50} /> : billingEndDate} secondary={capitalize(T.BILLING_END_DATE)} />
        </ListItem>
        <ListItem divider>
          <DetailItemIcon>
            <SvgIcon>
              <CalendarWeek />
            </SvgIcon>
          </DetailItemIcon>
          <DetailItemText
            primary={isLoading ? <Skeleton width={50} /> : pricingPeriod}
            secondary={capitalize(T.PRICE_PERIOD)}
            slotProps={{ primary: { variant: 'subtitle1', fontWeight: 500, noWrap: true }, secondary: { variant: 'caption' } }}
          />
          <DetailItemIcon>
            <SvgIcon>
              <CurrencyDollar />
            </SvgIcon>
          </DetailItemIcon>
          <DetailItemText
            primary={isLoading ? <Skeleton width={50} /> : pricingZone}
            secondary={capitalize(T.PRICING_ZONE)}
            slotProps={{ primary: { variant: 'subtitle1', fontWeight: 500, noWrap: true }, secondary: { variant: 'caption' } }}
          />
        </ListItem>
        <ListItem>
          <DetailItemIcon>
            <MoneyCheckDollarPenIcon width={24} height={24} />
          </DetailItemIcon>
          <ListItemText
            primary={isLoading ? <Skeleton width={50} /> : purchaseOrderNumber}
            secondary={T.PURCHASE_ORDER_LABEL}
            slotProps={{
              primary: { variant: 'subtitle1', fontWeight: 500 },
              secondary: { variant: 'caption' },
            }}
          />
        </ListItem>
      </Grid>
      <Grid size={{ xs: 12, md: 'grow' }}>
        <Stack spacing={1}>
          <Box>
            <LocationMapImage
              width="100%"
              height={200}
              latitude={parseFloat(latitude)}
              longitude={parseFloat(longitude)}
              containerId={`draft-cs-map-${serviceId}`}
              dynamicWidth
              showInvalidAlert={false}
              showPlaceholder
            />
          </Box>
          {(actionType || methodType) && (
            <Box display="flex" alignItems="center" gap={1}>
              {actionType && <ServiceActionTypeChip size="small" action={actionType} />}
              {methodType && <MethodChip methodType={methodType} />}
            </Box>
          )}
          {(actionType || methodType) && <Divider />}
          {(measureUnit || materialType) && (
            <Box display="flex" alignItems="center" gap={1}>
              {measureUnit && <MeasureChip label={measureLabel} measureUnit={measureUnit} />}
              {materialType && <ServiceMaterialTypeChip materialColor={materialColor} label={materialType} />}
            </Box>
          )}
          {containers.length > 0 && (
            <List sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <ListItem>
                <ListItemText
                  primary={upperCase(T.CONTAINERS)}
                  slotProps={{ primary: { variant: 'h6', fontWeight: 700 } }}
                />
                <ListItemText
                  primary="ID"
                  slotProps={{ primary: { variant: 'h6', fontWeight: 700, textAlign: 'right' } }}
                />
              </ListItem>
              {Children.toArray(
                containers.map(container => (
                  <ListItem>
                    <ListItemText
                      primary={get(container, 'containerName', '')}
                      slotProps={{ primary: { variant: 'h6', fontWeight: 500, noWrap: true } }}
                    />
                    <ListItemText
                      primary={get(container, 'containerDescription', '')}
                      slotProps={{ primary: { variant: 'h5', fontWeight: 500, noWrap: true, textAlign: 'right' } }}
                    />
                  </ListItem>
                ))
              )}
            </List>
          )}
        </Stack>
      </Grid>
    </Grid>
  )
}

const DraftServiceAccordionItemContent = ({ service, locationId = '', accountId = '' }) => {
  const {
    id,
    serviceName,
    serviceType,
    servicePriceCents,
    pricingPeriodInterval,
    pricingPeriodPer,
    unit: measureUnit,
    measure: measureVolume,
    methodType,
    quantity = 0,
    newContainerCount = 0,
  } = service
  const theme = useTheme()
  const navigate = useNavigate()
  const isInventoryFeatureEnabled = useSelector(selectIsInventoryFeatureEnabled, shallowEqual)
  const [actionMenuAnchorEL, setActionMenuAnchorEL] = useState(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedTab, setSelectedTab] = useState(SERVICE_DETAILS)

  const {
    isOpen: isEditPanelOpen,
    isDraft,
    isUpdating,
    isPublishing,
    hasUnsavedChanges,
    isRecurrencePublishable,
    recurrencePublishBlockerMessage,
    handleOpen: handleEditPanelOpen,
    handleClose: handleEditPanelClose,
    onSaveClick,
    onSaveAndPublishClick,
  } = useAdjustService()

  const containerCount = isInventoryFeatureEnabled ? newContainerCount : quantity
  const pricingPeriodText = pricingPeriodInterval === 'Instance' ? '' : getPricingPeriod(pricingPeriodPer, pricingPeriodInterval)
  const recurrenceLabel = useMemo(() => getServiceRecurrenceText(service), [service])
  const measureLabel = measureUnit && [measureVolume, getMeasureUnit(measureUnit)].join(' ')
  const MethodIcon = get(METHOD_TO_ICON_MAP, upperCase(methodType), null)

  const toggleIsExpanded = event => {
    event.stopPropagation()
    const next = !isExpanded
    setIsExpanded(next)
    if (next) {
      handleEditPanelOpen()
      setSelectedTab(EDIT)
    } else {
      handleEditPanelClose()
      setSelectedTab(SERVICE_DETAILS)
    }
  }

  const handleOpenActionMenu = event => {
    event.stopPropagation()
    setActionMenuAnchorEL(event.currentTarget)
  }

  const handleRowClick = event => {
    event.stopPropagation()
    const baseUrl = getConfiguredServiceDetailsPageUrl(id)
    const params = new URLSearchParams({ locationId, accountId }).toString()
    navigate(`${baseUrl}?${params}`)
  }

  const handleTabChange = (event, newValue) => {
    if (newValue === EDIT) {
      handleEditPanelOpen()
    } else {
      handleEditPanelClose()
    }
    setSelectedTab(newValue)
  }

  const handleEditService = () => {
    setIsExpanded(true)
    setSelectedTab(EDIT)
    handleEditPanelOpen()
  }

  const handleCancelEdit = () => {
    handleEditPanelClose()
    setSelectedTab(SERVICE_DETAILS)
  }

  const rowBGColor = useMemo(() => theme.palette.background.default, [theme])

  const handleCloseActionMenu = () => setActionMenuAnchorEL(null)

  return (
    <>
      <StyledAccordion
        elevation={0}
        expanded={isExpanded}
        disablePointer
        expandIcon={undefined}
        iconGap={false}
        slotProps={{ transition: { unmountOnExit: true } }}
      >
        <AccordionSummary
          sx={{ p: 0, backgroundColor: rowBGColor, '&.Mui-focusVisible': { background: 'inherit' }, cursor: 'pointer' }}
          onClick={handleRowClick}
        >
          <Box display="flex" alignItems="center" gap={1} width="100%" px={2} py={1}>
            <IconButton onClick={toggleIsExpanded}>{isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}</IconButton>

            <Box display="flex" flexDirection="column" flex={1} gap={0.5} sx={{ overflow: 'hidden', minWidth: 0 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  {MethodIcon && <MethodIcon width={24} height={24} color={theme.palette.text.secondary} style={{ minWidth: 24 }} />}
                  <Typography fontStyle="italic" color="textSecondary" variant="h4" fontWeight={600}>
                    {serviceName}
                  </Typography>
                  <Divider orientation="vertical" flexItem />
                  <Typography variant="h4" fontWeight={600} display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                    <HHDisplayMoney value={servicePriceCents} formatToDollars />
                    {pricingPeriodText && <Typography variant="subtitle2">{`/ ${pricingPeriodText}`}</Typography>}
                  </Typography>
                  <ActionBasedPricingIcon configuredServiceId={id} />
                </Box>
              </Box>

              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <Box display="flex" gap={1} alignItems="center">
                  <ServiceTypeAvatar serviceType={serviceType} isDraft />
                  <Typography variant="body2" noWrap>
                    {recurrenceLabel}
                  </Typography>
                  <ContainersChip variant="outlined" size="small" label={`Quantity | ${containerCount}`} />
                  {measureUnit && <MeasureChip label={measureLabel} measureUnit={measureUnit} />}
                  <WeeklySummary serviceType={serviceType} workOrders={[]} />
                </Box>
              </Box>
            </Box>

            <Box display="flex" justifyContent="center" alignItems="center">
              <IconButton size="small" onClick={handleOpenActionMenu}>
                <MoreVertIcon />
              </IconButton>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <Divider />
          <Box pl={2}>
            <Tabs value={selectedTab} onChange={handleTabChange} sx={{ button: { textTransform: 'capitalize' } }}>
              <Tab value={SERVICE_DETAILS} label={SERVICE_DETAILS} />
              <Tab value={EDIT} label={EDIT} />
            </Tabs>
          </Box>
          <TabPanel value={selectedTab} index={SERVICE_DETAILS}>
            <DraftServiceContent serviceId={id} />
          </TabPanel>
          <TabPanel value={selectedTab} index={EDIT}>
            <AdjustServicePanel serviceId={id} />
            {isEditPanelOpen && (
              <Stack direction="row" justifyContent="flex-end" gap={1} px={2} py={1} borderTop={1} borderColor="divider">
                <Button variant="text" color="textPrimary" size="small" onClick={handleCancelEdit} sx={{ textTransform: 'none' }}>
                  {T.CANCEL}
                </Button>
                <Tooltip
                  title={!hasUnsavedChanges ? T.NO_CHANGES_MADE : ''}
                  arrow
                  disableHoverListener={hasUnsavedChanges}
                  disableInteractive
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={onSaveClick}
                      disabled={isUpdating || isPublishing || !hasUnsavedChanges}
                      loading={isUpdating}
                      sx={{ textTransform: 'none' }}
                    >
                      {T.SAVE}
                    </Button>
                  </span>
                </Tooltip>
                {isDraft && (
                  <Tooltip
                    title={!isRecurrencePublishable ? recurrencePublishBlockerMessage || '' : ''}
                    arrow
                    disableHoverListener={isRecurrencePublishable}
                    disableInteractive
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={onSaveAndPublishClick}
                        disabled={isUpdating || isPublishing || !isRecurrencePublishable}
                        loading={isPublishing}
                        sx={{ textTransform: 'none' }}
                      >
                        {T.SAVE_AND_PUBLISH}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            )}
          </TabPanel>
        </AccordionDetails>
      </StyledAccordion>

      <DraftServiceActionMenu
        anchorEl={actionMenuAnchorEL}
        selectedService={service}
        locationId={locationId}
        accountId={accountId}
        onEditService={handleEditService}
        onClose={handleCloseActionMenu}
      />
    </>
  )
}

const DraftServiceAccordionItem = ({ service, locationId = '', accountId = '' }) => {
  const id = get(service, 'id')
  const dispatch = useDispatch()

  const handlePublishSuccess = useCallback(() => {
    dispatch(api.util.invalidateTags([CACHE_TAG_CONFIGURED_SERVICES_LIST, { type: CACHE_TAG_CONFIGURED_SERVICE_DETAILS, id }]))
  }, [dispatch, id])

  return (
    <AdjustServiceProvider serviceId={id} locationId={locationId} accountId={accountId} onPublishSuccess={handlePublishSuccess}>
      <DraftServiceAccordionItemContent service={service} locationId={locationId} accountId={accountId} />
    </AdjustServiceProvider>
  )
}

DraftServiceAccordionItem.propTypes = {
  service: PropTypes.object,
  locationId: PropTypes.string,
  accountId: PropTypes.string,
}

DraftServiceAccordionItemContent.propTypes = {
  service: PropTypes.object,
  locationId: PropTypes.string,
  accountId: PropTypes.string,
}

DraftServiceContent.propTypes = {
  serviceId: PropTypes.string.isRequired,
}

export default DraftServiceAccordionItem
